import { db } from '../firebase';
import { collection, doc, query, where, onSnapshot, getDocs } from 'firebase/firestore';

/**
 * Quiz Result Service
 *
 * Listens for quiz results from backend processing.
 * Backend creates a chat message with messageType: "quiz" containing
 * personality_type and summary as JSON string.
 */

/**
 * Listen for quiz result message from backend
 *
 * @param {string} sessionId - The Firestore session ID
 * @param {function} onResult - Callback when result arrives (receives parsed result object)
 * @param {function} onError - Error callback
 * @returns {function} Unsubscribe function to stop listening
 */
export const listenForQuizResult = (sessionId, onResult, onError) => {
  try {
    // Reference to the chats subcollection
    const sessionRef = doc(db, 'sessions', sessionId);
    const chatsRef = collection(sessionRef, 'chats');

    // Query for messages with messageType = "quiz"
    const q = query(chatsRef, where('chat.messageType', '==', 'quiz'));

    console.log('🔮 Starting to listen for quiz results in session:', sessionId);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        if (!querySnapshot.empty) {
          // Get the first quiz result message (should only be one)
          const resultDoc = querySnapshot.docs[0];
          const data = resultDoc.data();

          console.log('✅ Quiz result received:', data);

          try {
            // Parse the JSON message
            const resultMessage = JSON.parse(data.chat.message);

            console.log('📊 Parsed quiz result:', resultMessage);

            // Validate that we have the expected fields
            if (!resultMessage.personality_type || !resultMessage.summary) {
              console.error('❌ Invalid quiz result format:', resultMessage);
              onError(new Error('Invalid quiz result format'));
              return;
            }

            // Call the success callback with the parsed result
            onResult({
              personality_type: resultMessage.personality_type,
              summary: resultMessage.summary,
              summary_short: resultMessage.summary_short || null, // Short summary for mobile share
              rawData: data, // Include full document for reference
            });

            // Stop listening after receiving result
            unsubscribe();
          } catch (parseError) {
            console.error('❌ Error parsing quiz result message:', parseError);
            onError(parseError);
          }
        }
      },
      (error) => {
        console.error('❌ Error in quiz result listener:', error);
        onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('❌ Error setting up quiz result listener:', error);
    onError(error);
    return () => {}; // Return no-op unsubscribe function
  }
};

/**
 * Wait for quiz result with timeout
 *
 * Wrapper around listenForQuizResult that adds timeout functionality.
 * If no result arrives within the timeout period, calls onTimeout.
 *
 * @param {string} sessionId - The Firestore session ID
 * @param {function} onResult - Callback when result arrives
 * @param {function} onTimeout - Callback when timeout occurs
 * @param {function} onError - Error callback
 * @param {number} timeoutMs - Timeout in milliseconds (default 20000 = 20s)
 * @returns {function} Cleanup function to stop listening and clear timeout
 */
export const waitForQuizResultWithTimeout = (
  sessionId,
  onResult,
  onTimeout,
  onError,
  timeoutMs = 20000,
  keepListeningAfterTimeout = false // New option to keep listener alive after timeout
) => {
  let hasReceivedResult = false;
  let hasTimedOut = false;
  let timeoutId = null;
  let unsubscribe = null;

  // Set up timeout
  timeoutId = setTimeout(() => {
    if (!hasReceivedResult) {
      console.warn('⏰ Quiz result timeout after', timeoutMs, 'ms');
      hasTimedOut = true;

      // Only stop listening if NOT keeping listener alive
      if (!keepListeningAfterTimeout && unsubscribe) {
        unsubscribe();
      }

      // Call timeout callback
      onTimeout();
    }
  }, timeoutMs);

  // Set up listener
  unsubscribe = listenForQuizResult(
    sessionId,
    (result) => {
      // Result can arrive even after timeout if keepListeningAfterTimeout is true
      if (!hasReceivedResult) {
        hasReceivedResult = true;

        // Clear timeout if not already fired
        if (timeoutId && !hasTimedOut) {
          clearTimeout(timeoutId);
        }

        // Call result callback
        onResult(result);

        // If we kept listening after timeout, stop now
        if (hasTimedOut && unsubscribe) {
          unsubscribe();
        }
      }
    },
    (error) => {
      if (!hasReceivedResult) {
        hasReceivedResult = true;

        // Clear timeout if not already fired
        if (timeoutId && !hasTimedOut) {
          clearTimeout(timeoutId);
        }

        // Stop listening
        if (unsubscribe) {
          unsubscribe();
        }

        // Call error callback
        onError(error);
      }
    }
  );

  // Return cleanup function
  return () => {
    if (!hasReceivedResult) {
      hasReceivedResult = true;

      if (timeoutId && !hasTimedOut) {
        clearTimeout(timeoutId);
      }

      if (unsubscribe) {
        unsubscribe();
      }
    }
  };
};

/**
 * Fetch quiz result (one-time, non-realtime)
 *
 * Use this when loading results page via direct URL/deep link.
 * Unlike listenForQuizResult, this fetches once and returns.
 *
 * @param {string} sessionId - The Firestore session ID
 * @returns {Promise<{personality_type: string, summary: string, summary_short: string|null} | null>}
 */
export const fetchQuizResult = async (sessionId) => {
  try {
    console.log('🔍 Fetching quiz result for session:', sessionId);

    // Reference to the chats subcollection
    const sessionRef = doc(db, 'sessions', sessionId);
    const chatsRef = collection(sessionRef, 'chats');

    // Query for messages with messageType = "quiz"
    const q = query(chatsRef, where('chat.messageType', '==', 'quiz'));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('📭 No quiz result found for session:', sessionId);
      return null;
    }

    // Get the first quiz result message (should only be one)
    const resultDoc = querySnapshot.docs[0];
    const data = resultDoc.data();

    console.log('✅ Quiz result fetched:', data);

    // Parse the JSON message
    const resultMessage = JSON.parse(data.chat.message);

    console.log('📊 Parsed quiz result:', resultMessage);

    // Validate that we have the expected fields
    if (!resultMessage.personality_type || !resultMessage.summary) {
      console.error('❌ Invalid quiz result format:', resultMessage);
      return null;
    }

    return {
      personality_type: resultMessage.personality_type,
      summary: resultMessage.summary,
      summary_short: resultMessage.summary_short || null,
    };
  } catch (error) {
    console.error('❌ Error fetching quiz result:', error);
    throw error;
  }
};

export default {
  listenForQuizResult,
  waitForQuizResultWithTimeout,
  fetchQuizResult,
};
